import { HttpClientModule, HttpHeaders } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { ApolloClientOptions, ApolloLink, InMemoryCache, from } from '@apollo/client/core';
import { CoreModule } from '@app/core/core.module';
import { environment } from '@app/environments/environment';
import { routes } from '@app/routes/app.routes';
import { BrandModule } from '@app/shared/brand/brand.module';
import { CreativesetComponent, CreativesetShowcaseComponent } from '@app/shared/creativeset';
import { CreativesetModule } from '@app/shared/creativeset/creativeset.module';
import { FiltersModule } from '@app/shared/filters/filters.module';
import { FontFamiliesModule } from '@app/shared/font-families/font-families.module';
import { SharedModule } from '@app/shared/shared.module';
import { UserSettingsModule } from '@app/shared/user-settings/user-settings.module';
import { UserModule } from '@app/shared/user/user.module';
import { VersionsModule } from '@app/shared/versions/versions.module';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { Container } from '@studio/utils/di';
import { APOLLO_NAMED_OPTIONS, APOLLO_OPTIONS, ApolloModule } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { AppComponent } from './app.component';
import { Error404Component, MaintenanceComponent, NavigationGuard, OopsComponent } from './pages';
import { metaReducers } from './pages/state/local-storage';
import { CreativesModule } from './shared/creatives/creatives.module';
import { StudioHubService } from './shared/services/studio-hub.service';

@NgModule({
    declarations: [
        // Bootstrapping
        AppComponent,
        CreativesetComponent,
        CreativesetShowcaseComponent,

        // Misc Pages
        Error404Component,
        OopsComponent,
        MaintenanceComponent
    ],
    imports: [
        BrowserAnimationsModule,
        BrowserModule,
        HttpClientModule,
        FormsModule,
        ReactiveFormsModule,
        ApolloModule,
        CoreModule,
        SharedModule,
        StoreModule.forRoot(
            {},
            {
                metaReducers,
                runtimeChecks: {
                    strictActionImmutability: true,
                    strictStateImmutability: true
                }
            }
        ),
        EffectsModule.forRoot([]),
        StoreDevtoolsModule.instrument({
            logOnly: environment.production
        }),
        StoreRouterConnectingModule.forRoot(),
        BrandModule,
        VersionsModule,
        UserModule,
        UserSettingsModule,
        FontFamiliesModule,
        FiltersModule,
        CreativesModule,
        CreativesetModule,
        RouterModule.forRoot(routes)
    ],
    bootstrap: [AppComponent],
    providers: [
        {
            provide: Container,
            useValue: new Container()
        },
        {
            provide: APOLLO_OPTIONS,
            useFactory: (
                httpLink: HttpLink,
                studioHubService: StudioHubService
            ): ApolloClientOptions<any> => {
                return {
                    cache: new InMemoryCache({
                        typePolicies: {
                            // Same ID doesn't mean same property
                            VersionPropertyType: { keyFields: false }
                        }
                    }),
                    link: from([
                        new ApolloLink((operation, forward) => {
                            operation.setContext({
                                headers: new HttpHeaders().set(
                                    'BF-Studio-SignalR-ConnectionId',
                                    studioHubService.connectionId || ''
                                )
                            });

                            return forward(operation);
                        }),
                        httpLink.create({ uri: `${environment.origins.sapi}/graphql` })
                    ]),
                    connectToDevTools: true,
                    defaultOptions: {
                        mutate: {
                            fetchPolicy: 'no-cache'
                        },
                        query: {
                            fetchPolicy: 'no-cache'
                        }
                    }
                };
            },
            deps: [HttpLink, StudioHubService]
        },
        {
            provide: APOLLO_NAMED_OPTIONS,
            useFactory: (httpLink: HttpLink): Record<string, ApolloClientOptions<any>> => {
                return {
                    showcase: {
                        name: 'showcase',
                        link: httpLink.create({ uri: `${environment.origins.sapi}/graphql/showcase` }),
                        cache: new InMemoryCache({
                            typePolicies: {
                                // Same ID doesn't mean same property
                                VersionPropertyType: { keyFields: false }
                            }
                        })
                    }
                };
            },
            deps: [HttpLink]
        },
        Title,
        NavigationGuard
    ]
})
export class AppModule {}
